import { PromptTypes } from "@kinde/js-utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { KINDE_ORG } from "@next-platform/client-common-core/infra/constants";
import { useEffect } from "react";

import silentSsoHandoffLogger from "@/components/SilentSsoHandoff/silentSsoHandoffLogger";
import { AUTH_HANDOFF_PARAM, AUTH_HANDOFF_VALUE, NEXT_PAGE_UPON_REDIRECT, SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY } from "@/constants";

// Module-scoped guard: StrictMode/HMR/Suspense remounts must not retrigger
// silent auth mid-flight (the in-progress redirect would race the second call).
let hasSilentSsoBootRun = false;

function stripAuthHandoffParamFromUrl(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(AUTH_HANDOFF_PARAM)) return;
  url.searchParams.delete(AUTH_HANDOFF_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
}

function safelyStripAuthHandoffParamFromUrl(): void {
  try {
    stripAuthHandoffParamFromUrl();
  } catch (error) {
    silentSsoHandoffLogger.warn("strip failed; param may persist in URL", { error });
  }
}

function readAuthHandoffSignal(): boolean {
  return new URLSearchParams(window.location.search).get(AUTH_HANDOFF_PARAM) === AUTH_HANDOFF_VALUE;
}

// Snapshot the path under the keys KINDE_PROVIDER_CALLBACKS.onSuccess already
// reads. The Kinde SDK's redirect_uri is fixed at window.location.origin, so
// without this the post-auth landing always collapses to "/". au_h is dropped
// from the snapshot to prevent re-triggering silent auth on the bounce-back.
function rememberPostAuthDestination(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(AUTH_HANDOFF_PARAM);
    localStorage.setItem(NEXT_PAGE_UPON_REDIRECT, `${url.pathname}${url.search}${url.hash}`);
    localStorage.setItem(SHOULD_REDIRECT_USER_LOCAL_STORAGE_KEY, "true");
  } catch (error) {
    silentSsoHandoffLogger.warn("could not store post-auth destination", { error });
  }
}

type SilentSsoHandoffDeps = {
  isAuthenticated: boolean;
  isLoading: boolean;
  kindeLogin: ReturnType<typeof useKindeAuth>["login"];
};

function attemptSilentSsoHandoff(deps: SilentSsoHandoffDeps): boolean {
  const { isAuthenticated, isLoading, kindeLogin } = deps;

  if (isLoading) return false;
  if (!readAuthHandoffSignal()) return true;
  if (isAuthenticated) {
    safelyStripAuthHandoffParamFromUrl();
    return true;
  }

  rememberPostAuthDestination();
  safelyStripAuthHandoffParamFromUrl();

  // kindeLogin returns a promise; chain .catch() so async rejections are
  // handled (a try/catch around `void promise` would only catch sync throws).
  Promise.resolve()
    .then(() => kindeLogin({ prompt: PromptTypes.none, orgCode: KINDE_ORG }))
    .catch((error: unknown) => {
      silentSsoHandoffLogger.warn("kindeLogin({ prompt: none }) failed", { error });
    });
  return true;
}

/**
 * Consumes the funnel's `au_h=1` handoff signal and silently exchanges the
 * shared Kinde session cookie at `auth.<brand>.ai` for brand-app tokens.
 * Mount once inside `<KindeProvider>`.
 */
const SilentSsoHandoff = (): null => {
  const { isAuthenticated, isLoading, login: kindeLogin } = useKindeAuth();

  useEffect(() => {
    if (hasSilentSsoBootRun) return;
    const isResolved = attemptSilentSsoHandoff({ isAuthenticated, isLoading, kindeLogin });
    if (isResolved) {
      hasSilentSsoBootRun = true;
    }
  }, [isAuthenticated, isLoading, kindeLogin]);

  return null;
};

export default SilentSsoHandoff;
